"use client";

import { createRestDataProviderClient } from "@providers/data-provider/rest-data-provider.client";
import { isFilterValueExist, parseFilter, transformQueryParamsWithoutLocale } from "@shared/utils";
import { Checkbox, ComboboxOption } from "@ubud-ui/react-ui-kit";
import { useQuery } from "@tanstack/react-query";
import { useRouter, useSearchParams } from "next/navigation";
import qs from "qs";

type Physical = {
  id: string;
  name: string;
};

type Disability = {
  id: string;
  key: string;
  name: string;
};

const FILTER_KEY = "physicals";
const DISABILITY_FILTER_KEY = "disabilities";

export function PhysicalConditionFilter({ path }: { path: string }) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const physicalResult: Physical[] = [
    {
      id: "normal",
      name: "Non Disabilitas",
    },
    {
      id: "disabled",
      name: "Disabilitas",
    },
  ];

  const restDataProviderClient = createRestDataProviderClient();

  const { data: disabilityResult } = useQuery<{ data: Disability[] }>({
    queryKey: ["vacancy-physical-condition-disability-filter"],
    queryFn: () =>
      restDataProviderClient.custom({
        url: transformQueryParamsWithoutLocale("/karirhub/v1/disabilities", {
          limit: 100,
        }),
        method: "GET",
      }),
  });

  const physicals: ComboboxOption[] = physicalResult.map((it) => {
    return {
      value: it.id,
      label: it.name,
    };
  });

  const disabilities: ComboboxOption[] = disabilityResult?.data
    ? disabilityResult?.data?.map((it) => {
        return {
          value: it.key,
          label: it.name,
        };
      })
    : [];

  const handleFilterPhysical = (option: ComboboxOption, checked: boolean) => {
    const paramsString = searchParams.toString();

    const value = {
      id: option.value,
      label: option.label,
    };

    const params = parseFilter(FILTER_KEY, value, qs.parse(paramsString), checked);

    if (option.value === "disabled" && !checked) {
      params[DISABILITY_FILTER_KEY] = null;
    }

    router.push(`${path}?${qs.stringify(params, { skipNulls: true })}`, {
      scroll: false,
    });
  };

  const handleFilterDisability = (option: ComboboxOption, checked: boolean) => {
    const paramsString = searchParams.toString();

    const value = {
      id: option.value,
      label: option.label,
    };

    const params = parseFilter(DISABILITY_FILTER_KEY, value, qs.parse(paramsString), checked);

    router.push(`${path}?${qs.stringify(params, { skipNulls: true })}`, {
      scroll: false,
    });
  };

  const isPhysicalChecked = (value: string) => {
    const params = qs.parse(searchParams.toString());

    return isFilterValueExist(params, FILTER_KEY, value);
  };

  const isDisabilityChecked = (value: string) => {
    const params = qs.parse(searchParams.toString());

    return isFilterValueExist(params, DISABILITY_FILTER_KEY, value);
  };

  return (
    <>
      {physicals && physicals.length > 0 ? (
        <div className="space-y-3 pb-2">
          {physicals.map((option, index) => {
            return (
              <div key={option.value + "-" + index}>
                <div className="flex items-start gap-3">
                  {isPhysicalChecked(option.value)}
                  <Checkbox
                    className="mt-1"
                    id={option.value + "-" + index}
                    onCheckedChange={(checked) => handleFilterPhysical(option, checked as boolean)}
                    checked={isPhysicalChecked(option.value)}
                  />
                  <label htmlFor={option.value + "-" + index}>{option.label}</label>
                </div>
                {isPhysicalChecked(option.value) && option.value === "disabled" && disabilities && disabilities.length > 0 && (
                  <div className="ml-7 space-y-3 mt-3">
                    {disabilities.map((disability, i) => {
                      return (
                        <div className="flex items-start gap-3" key={disability.value + "-" + i}>
                          {isDisabilityChecked(disability.value)}
                          <Checkbox
                            className="mt-1"
                            id={disability.value + "-" + i}
                            onCheckedChange={(checked) => handleFilterDisability(disability, checked as boolean)}
                            checked={isDisabilityChecked(disability.value)}
                          />
                          <label htmlFor={disability.value + "-" + i}>{disability.label}</label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <></>
      )}
    </>
  );
}
